/**
 * Games Info View
 */

define(function(require, exports, module) {
    'use strict';

    var $ = require('j');
    var _ = require('underscore');
    var core = require('core');
    var user = require('user');
    var PortletView = require('modules/portlet/portlet.view');
    var Swipe = require('class/swipe.class');
    var mathAlgorithms = require('util/math.util').algorithms;
    var device = require('util/device.util');
    var Form = require('class/form.class');
    var favoritesMixin = require('mixins/favorites.mixin');
    var gameLaunchTrackMixin = require('mixins/gameTrack.mixin');
    var favoritesStore = require('stores/favorites/favorites.store.instance');
    var history = require('util/history.util');
    var url = require('util/url.util');
    var storage = require('util/storage.util');
    var gameTracker = require('modules/trackers/game.tracker.instance');
    var jackpotModel = require('replacers/jackpot/jackpot.model.instance');
    var config = require('config');
    var DynamicGameImages = require('portlets/games-info/gamesinfo.dynamic-game-images');
    var layoutHelper = require('portlets/games-info/gamesinfo.game.layout.helper');
    var GamesInfoAnimation = require('portlets/games-info/gamesinfo.animation');
    var scrollManager = require('managers/scroll.manager');

    require('widgets/paging.widget');
    require('widgets/ticker.widget');
    require('widgets/search.widget');
    require('widgets/scale.widget');

    /**
     * @class
     * @name GamesInfoView
     * @augments PortletView
     * @mixes Cocktail.mixins.favorites
     */
    module.exports = PortletView.extend(/** @lends GamesInfoView.prototype */ {

        mixins: [favoritesMixin, _.omit(gameLaunchTrackMixin, 'events')],

        /**
         * Cell is a part of a game tile and is `li.gamesinfo__item_style_extended` when game tile has 1x1 size
         * Container is `.fn-game-items`
         */
        _customLayoutCssValues: { // in em
            cellWidth: 24.25,
            cellHeight: 19.5,
            cellMarginL: 0.5,
            cellMarginR: 0.5,
            cellMarginT: 0,
            cellMarginB: 2,
            containerPaddingL: 0.25,
            containerPaddingR: 0.25,
            containerPaddingT: 1
        },

        pagingOptions: {
            type: 'bullet',
            arrows: true,
            infinity: false
        },

        className: function() {
            var className = 'gamesinfo';

            if (this._isShowCategories()) {
                className += ' gamesinfo_contains_categories';
            }

            return className;
        },

        initialize: function() {
            PortletView.prototype.initialize.apply(this, arguments);

            this.trackers = [gameTracker];
            this.dynamicGameImages = new DynamicGameImages(this);

            if (config.get('animationConfig.animationEnabled')) {
                this.animation = new GamesInfoAnimation(this);
            }
        },

        subscribe: function() {
            this.listenTo(this.model, 'change:sortField', this.renderGames);
            this.listenTo(this.model.categories, 'change:isLoading change:active change:games',
                this._handleCategoriesChange);
            this.listenTo(favoritesStore, 'add remove', this.handleUpdateFavorites);
            this.listenTo(jackpotModel, 'change', this._onJackpotsChange);
            this.listenTo(core, {
                'popup:rendered': this._onPopupRendered.bind(this),
                'gameAdviserSuggestions:fetched': this.checkIfNeedToTrackPersonalizedCategory.bind(this),
                'scaleWidget:update': this.updateScalingWidget.bind(this)
            });

            // Category resize
            $(window)
                .on('resize.gamesinfo', _.throttle(this.setCategoryTabsWidth.bind(this), 200))
                .on('resize.gamesinfo', this.handleGamesContainerResize.bind(this))
                .on('resize.gamesinfo', _.debounce(this.fixLastRowItemsDisplay.bind(this), 200));
        },

        events: _.extend({}, {
            'tap .fn-favorites': 'toggleGameFavorites',
            'tap .fn-category': '_onCategoryClick',
            'tap .fn-view-mode-item': 'changeViewMode',
            'tap .fn-game-item': 'handleGameItemTap',
            'change .fn-sort-games': 'changeGameSorting',
            'tap .fn-launch-game[disabled]': '_handleTapOnDisabledGameMode'
        }, gameLaunchTrackMixin.events),

        render: function() {
            if (config.get('animationConfig.animationEnabled')) {
                this.animation._isFirstTimeShow = true;
            }

            this.$el.html(this.tmpl('gamesinfo.portlet', this.options));

            this._updateLayoutMap();
            this.renderCategories();
            this.renderControls();
            this.renderGames();

            return this;
        },

        checkIfNeedToTrackPersonalizedCategory: function(gameAdviserFallback) {
            var activeCategoryModel = this.model.categories.getActive();
            var activeCategory = activeCategoryModel.toJSON();

            if (activeCategory.enableDynamicCategory) {
                this._trackPersonalizedGridEventForActiveCategory(activeCategory, gameAdviserFallback);
            }
        },

        _onPopupRendered: function() {
            // not supported anymore:
            // if game's portlet in popup page
            // if (PopupView.getCurrentPagePopup()) {
            //     this.setCategoryTabsWidth();
            // }

            this.fixLastRowItemsDisplay();
        },

        _trackPersonalizedGridEventForActiveCategory: function(activeCategory, gameAdviserFallback) {
            this.track(
                'gamePortletPersonalizedGrid',
                {
                    container: this.options && this.options.portletId,
                    Feed_Name: this.options && this.options.categoriesFeedName,
                    Page_Path: url.friendlyUrl,
                    Personalized_Category_Name: activeCategory.name,
                    Personalized_Grid_Fallback: gameAdviserFallback
                }
            );
        },

        portletDidMount: function() {
            this.setCategoryTabsWidth();
            this.addScalingWidget();
            this.fixLastRowItemsDisplay();
            this._addGamesJackpots();
            this.resizeCustomLayout();
            this.fixPageHeight();
            this.dynamicGameImages.enable();
        },

        renderControls: function() {
            var $controls = this.$('.fn-controls');
            var controlsConfig = {
                viewModes: this._getViewModesConfig(),
                search: this._getSearchConfig(),
                sorting: this._getSortingConfig()
            };

            $controls.html(this.tmpl('gamesinfo.controls', controlsConfig));

            this.renderSearch();

            Form.prototype.wrapFormControls($controls);

            if (config.get('animationConfig.animationEnabled') && config.get('animationConfig.gamesinfoSearch')) {
                this.$('.gamesinfo__search-wrapper').addClass('gamesinfo__search-wrapper--animate');
            }
        },

        renderSearch: function() {
            var searchConfig = this._getSearchConfig();
            var $search = this.$('.fn-search');

            if (!searchConfig.enabled) {
                return;
            }

            this.detachWidget($search, 'search');
            this.attachWidget($search.search(this.getSearchWidgetOptions()));

            $search
                .off('.search')
                .on('search:changed.search', _.debounce(function(event) {
                    this.track(
                        'gamePortletSearch',
                        { searchString: event.searchString }
                    );
                }.bind(this), 1500));
        },

        /**
         * Get options for Search Widget
         * Can be used for overriding source of searchable fields
         * @return {Object}
         */
        getSearchWidgetOptions: function() {
            var queryString = this.model.get('additionalQueryString');
            var games = this.model.categories.getGameConfigurations();
            var addAdditionalQueryString = function(game) {
                return _.extend({}, game, { additionalQueryString: queryString });
            };

            return {
                labelField: 'name',
                source: {
                    data: queryString ? games.map(addAdditionalQueryString) : games,
                    fields: ['name']
                },
                type: 'game'
            };
        },

        _getViewModesConfig: function() {
            var viewModesConfig = {
                enabled: this.options.listViewOptionEnabled
            };

            if (!viewModesConfig.enabled) {
                return viewModesConfig;
            }

            viewModesConfig.items = [
                {
                    modeId: 'ITEM',
                    label: this.translate('playtech.mobile.games-info.view-modes.items'),
                    active: false
                },
                {
                    modeId: 'DETAILED_LIST',
                    label: this.translate('playtech.mobile.games-info.view-modes.detailed'),
                    active: false
                },
                {
                    modeId: 'LIST',
                    label: this.translate('playtech.mobile.games-info.view-modes.list'),
                    active: false
                }
            ];

            var activeItem =
                _.findWhere(viewModesConfig.items, { modeId: this.options.viewMode }) || viewModesConfig.items[0];

            activeItem.active = true;

            return viewModesConfig;
        },

        _handleTapOnDisabledGameMode: function(e) {
            //workaround for IE
            e.stopImmediatePropagation();
        },

        _handleCategoriesChange: function(categoryModel) {
            this._updateLayoutMap();
            if (this.model.categories.getActiveName() === categoryModel.get('name')) {
                this.renderCategories();
                this.renderControls();
                this.renderGames();
            }

            if (categoryModel.get('enableDynamicCategory')) {
                this.renderSearch();
            }
        },

        _getSearchConfig: function() {
            var searchConfig = {
                enabled: this.options.showSearch || false
            };

            return searchConfig;
        },

        _getSortingConfig: function() {
            var sortingConfig = {
                enabled: this.options.sortBy && !this.model.categories.getActive().get('enableDynamicCategory')
            };

            if (!sortingConfig.enabled) {
                return sortingConfig;
            }

            sortingConfig.fields = [
                {
                    label: this.translate('playtech.mobile.games-info.sorting-fields.default'),
                    code: 'default',
                    active: false
                },
                {
                    label: this.translate('playtech.mobile.games-info.sorting-fields.popular'),
                    code: 'popular',
                    active: false
                },
                {
                    label: this.translate('playtech.mobile.games-info.sorting-fields.new'),
                    code: 'new',
                    active: false
                },
                {
                    label: this.translate('playtech.mobile.games-info.sorting-fields.jackpot'),
                    code: 'jackpotCode',
                    active: false
                },
                {
                    label: this.translate('playtech.mobile.games-info.sorting-fields.alphabetical'),
                    code: 'name',
                    active: false
                }
            ];

            if (user.isLoggedIn() && this.model.get('useFavoritesEnabled')) {
                sortingConfig.fields.splice(3, 0, {
                    label: this.translate('playtech.mobile.games-info.sorting-fields.favorites'),
                    code: 'isInFavorites',
                    active: false
                });
            }

            var activeItem = _.findWhere(sortingConfig.fields, { code: this.model.get('sortField') })
                || sortingConfig.fields[0];
            activeItem.active = true;

            return sortingConfig;
        },

        /**
         * Render game list
         * @return {GamesInfoView}
         */
        renderGames: function() {
            if (config.get('animationConfig.animationEnabled')) {
                this.animation.prepareAnimations();
            }

            var activeCategory, gamesHtml, itemTemplate, limitPerSlide, template;
            var additionalQueryString = this.model.get('additionalQueryString');

            this.$games = this.$('.fn-games');

            this.dynamicGameImages.disable();

            if (!this.model.categories.length) {
                return this;
            }

            // prevent scrolling top on dynamic category load or other such nuisances
            var lock = this.cid + '_renderGames';
            scrollManager.disableScrolling(lock);

            var activeCategoryModel = this.model.categories.getActive();

            if (this.options.sortBy && !activeCategoryModel.get('enableDynamicCategory')) {
                activeCategoryModel.sortGames(this.model.get('sortField'));
            }

            activeCategory = activeCategoryModel.toJSON();
            activeCategory.games = this.model.getCategoryGamesOrFilterThemByCMS(activeCategory);

            template = 'gamesinfo.gameitems.' + this.options.viewMode.toLowerCase();
            itemTemplate = this._getItemTemplate(template);
            limitPerSlide = this._getLimitPerSlide();

            var templateAttrs = {
                category: activeCategory,
                games: this.model.games,
                navigationType: this.options.navigationType,
                limitPerSlide: limitPerSlide,
                showJackpots: this.model.get('showJackpots'),
                isDesktop: device.formFactor === 'desktop',
                itemView: this.options.itemView,
                isError: this.model.get('isError') || false,
                isResponsive: this.options.responsiveGrid,
                col: this.options.gamesNumberOfColumns,
                row: this.options.gamesNumberOfRows,
                isFavoritesAllowed: user.isLoggedIn() && this.options.useFavoritesEnabled,
                isDisabledDemoMode: user.isLoggedIn() && this.options.disableDemoWhenLoggedIn,
                itemTemplate: itemTemplate,
                model: this.model.toJSON(),
                articles: Playtech.Variables.gamesArticles,
                layoutMap: this.layoutMap
            };

            gamesHtml = this.tmpl(template, templateAttrs);

            this._removeGamesJackpots();
            this.removeScalingWidget();
            this.removePaging();

            this.$games[0].innerHTML = gamesHtml;

            this.$games
                .off('.paging')
                .on('paging:pageChanged.paging', this._handlePageChangeStart.bind(this))
                .on('paging:endAnimation.paging', this._handlePageChangeEnd.bind(this));

            if (additionalQueryString) {
                this.$games
                    .find('.fn-launch-game')
                    .attr('data-query-string', additionalQueryString);
            }

            activeCategory.games && this.renderPaging(activeCategory.games.length, limitPerSlide);

            this.addScalingWidget();
            this.fixLastRowItemsDisplay();
            this._addGamesJackpots();

            if (this.portletHasMounted) {
                this.resizeCustomLayout();
                this.fixPageHeight();
                this.dynamicGameImages.enable();
            }

            scrollManager.enableScrolling(lock);

            if (config.get('animationConfig.animationEnabled')) {
                this.animation.runAnimations();
            }

            return this;
        },

        _getLimitPerSlide: function() {
            if (this.layoutMap) {
                return this.layoutMap.cellCount;
            }
            return this.options.gamesNumberOfColumns * this.options.gamesNumberOfRows;
        },

        _getItemTemplate: function(template) {
            if (this.getIsCustomLayout()) {
                return template + '.extended.custom-layout';
            }
            return this.options.viewMode === 'ITEM' ? template + '.' + this.options.itemView.toLowerCase() : '';
        },

        _handlePageChangeEnd: function() {
            this._resumeVisibleGamesJackpots();
            this.dynamicGameImages.enable();
        },

        _handlePageChangeStart: function(e) {
            var originalTarget = e.originalTarget;
            var gamePortletPaginationType;

            this._stopGamesJackpots();

            if (!originalTarget) {
                gamePortletPaginationType = 'slide';
            } else if (originalTarget.classList.contains('fn-bullet')) {
                gamePortletPaginationType = 'bullet';
            } else {
                gamePortletPaginationType = 'arrows';
            }

            if (this.$games.paging('option', 'activePage') !== this.$games.paging('option', 'beforePage')) {
                this.track(
                    'gamePortletPage',
                    {
                        feedName: this.options.categoriesFeedName,
                        gamePortletPaginationType: gamePortletPaginationType,
                        pageElementClicked: originalTarget
                    }
                );
            }

            this.dynamicGameImages.disable();
        },

        getPagingData: function(pageCount) {
            return {
                type: 'bullet',
                activePage: 0,
                lengthOfPages: pageCount,
                arrows: true,
                infinity: false
            };
        },

        renderPaging: function(gameCount, limitPerPage) {
            var pageCount;

            if (!(this.options.viewMode === 'ITEM' && this.options.navigationType === 'SLIDER')) {
                return;
            }

            pageCount = Math.ceil(gameCount / limitPerPage);

            if (pageCount > 1) {
                this.attachWidget(this.$games.paging(this.getPagingData(pageCount)));

                this.options.hasPaging = true;

                if (this.portletHasMounted) {
                    this.fixPageHeight();
                } else {
                    this.once('portlet:mounted', this.fixPageHeight.bind(this));
                }
            }
        },

        removePaging: function() {
            this.options.hasPaging = false;

            this.detachWidget(this.$games, 'paging');
        },

        fixPageHeight: function() {
            if (this.$games && !this.$games.isWidgetAttached('paging')) {
                return;
            }

            const items = this.$el.find('.fn-game-items');
            const list = this.$el.find('.fn-paging-list');
            const pages = this.$el.find('.fn-paging-pages');

            if (items.length) {
                _.last(items).style.height = items[0].offsetHeight + 'px';
                pages[0].style.paddingBottom = `${list[0].offsetHeight}px`;
            }
        },

        fixLastRowItemsDisplay: function() {
            var lock = this.cid + '_fixLastRowItemsDisplay';

            if (device.formFactor !== 'desktop') {
                return;
            }

            scrollManager.disableScrolling(lock);

            // magic number by O.Tsymbaliuk (COR-4824: "7 lines of Game description")
            var GAMESINFO_BOTTOM_MARGIN = '180px';
            var $gameItemsBlockSlider = this.$('.gameitems-block--slider .fn-paging-pages');
            $gameItemsBlockSlider.css('margin-bottom', '');
            var elements = this.$('.fn-style-extended')
                .removeClass('gamesinfo__item--last-row')
                .map(function(element) {
                    return {
                        item: element,
                        clientRect: element.getBoundingClientRect()
                    };
                });

            if (!elements.length) {
                scrollManager.enableScrolling(lock);
                return;
            }

            var max = _.max(elements, function(element) {
                return element.clientRect.bottom;
            });
            var lastRowItems = _.filter(elements, function(element) {
                return element.clientRect.bottom === max.clientRect.bottom;
            });

            var isSingleRow = lastRowItems.length === elements.length;
            if (!isSingleRow) {
                $(_.pluck(lastRowItems, 'item')).addClass('gamesinfo__item--last-row');
            }

            var needAddBottomMargin = isSingleRow
                && this.options.viewMode === 'ITEM'
                && this.options.itemView === 'EXTENDED'
                && this.options.navigationType === 'SLIDER';

            if (needAddBottomMargin) {
                $gameItemsBlockSlider.css('margin-bottom', GAMESINFO_BOTTOM_MARGIN);
            }

            scrollManager.enableScrolling(lock);
        },

        _isShowCategories: function() {
            return this.model.get('navigationStyle') === 'MENU' && this.model.categories.length;
        },

        /**
         * Set category tabs full width of screen, if width is less than screen
         */
        setCategoryTabsWidth: function() {
            if (!document.body.contains(this.el)
                || !this._isShowCategories()
                || this.options.navigationPosition === 'LEFT'
                || !this.$('.fn-nav').length
            ) {
                return;
            }

            var portletWidth = this.el.offsetWidth;
            var navWidth = this.$('.fn-nav')[0].offsetWidth;
            var categoriesCount = this.model.categories.length;
            var categoryWidth = 100 / categoriesCount + '%';

            this.initialNavWidth = this.initialNavWidth || navWidth;

            if (this.initialNavWidth < portletWidth) {
                this.$('.fn-navigation').addClass('js-navigation-fixed');
                this.$('.fn-category').css({ 'width': categoryWidth });
            } else {
                this.$('.fn-navigation').removeClass('js-navigation-fixed');
                this.$('.fn-category').css({ 'width': 'auto' });
            }
        },

        /**
         * Render category list
         * @return {GamesInfoView}
         */
        renderCategories: function() {
            var tmplPath = 'gamesinfo.navigation' + (this.options.navigationPosition === 'LEFT' ? '.left' : '');
            var expected = 0;
            var easing;
            var lastChange;
            var force;
            var time;
            var templateAttrs = {
                categories: this.model.categories.toJSON(),
                activeCategory: this.model.categories.getActiveName()
            };

            if (!this._isShowCategories()) {
                return;
            }

            this.$('.fn-navigation').html(this.tmpl(tmplPath, templateAttrs));

            this.categorySwipe = new Swipe(this.$('.fn-nav'), {
                detectingStrategy: function(swipe, touch) {
                    var isDetecting = Swipe.detectingStrategies.x.call(swipe, swipe, touch);
                    var isSwipe = this.checkSwipeCategory();

                    return isDetecting && isSwipe;
                }.bind(this)
            });

            $.event.add(window, 'orientationchange.gamesinfo', function() {
                _.delay(this.checkSwipeCategory.bind(this, true), 300);
            }.bind(this));

            if (this.portletHasMounted) {
                this.setCategoryTabsWidth();
                this.checkSwipeCategory(true);
            }

            this.categorySwipe.on({
                move: function(item) {
                    force = Math.abs(lastChange - item.delta.x);
                    force = Math.round(force * force / 100);
                    item.translate(item.start.x - item.delta.x, 0);
                    lastChange = item.delta.x;
                },

                end: function(item) {
                    easing = mathAlgorithms.easeInQuad;
                    time = 300;

                    if (item.position.x > 0) {
                        expected = 0;
                    } else if (item.el.clientWidth + item.position.x < document.body.clientWidth) {
                        expected = this.$('.fn-navigation')[0].clientWidth - item.el.clientWidth;
                    } else {
                        time = force * 300;
                        expected = item.position.x + force * 50 * (item.delta.x > 0 ? -1 : 1);
                        easing = mathAlgorithms.easeOutQuint;

                        if (expected > 0) {
                            expected = 0;
                            time = 300;
                        } else if (item.el.clientWidth + expected < document.body.clientWidth) {
                            expected = document.body.clientWidth - item.el.clientWidth;
                            time = 300;
                        }
                    }
                    item.animateTo(expected, 0, time, easing);
                }.bind(this)

            }, this);

            return this;
        },

        /**
         * Check for need in swipe of games category
         * @return {Boolean}
         */
        checkSwipeCategory: function(fitToScreen) {
            var navigationViewportEl = this.$('.fn-navigation')[0];
            var navigationCategoriesList = this.categorySwipe.el;
            var translateXValue = 0;
            var navigationCategoriesListCategories = navigationCategoriesList.childNodes;
            var navigationActiveCategory = this.categorySwipe.el.querySelector('.fn-category.active');
            var navigationActiveCategoryIndex = [].indexOf.call(navigationCategoriesListCategories,
                navigationActiveCategory);
            var navigationActiveCategoryPlusPreviousSiblingsWidth = 0;
            var i;

            if (navigationCategoriesList.clientWidth > document.body.clientWidth) {
                if (fitToScreen) {
                    for (i = 0; i <= navigationActiveCategoryIndex; i++) {
                        navigationActiveCategoryPlusPreviousSiblingsWidth +=
                            navigationCategoriesListCategories[i].clientWidth;
                    }
                    if (navigationActiveCategoryPlusPreviousSiblingsWidth > navigationViewportEl.clientWidth) {
                        translateXValue = navigationViewportEl.clientWidth
                            - navigationActiveCategoryPlusPreviousSiblingsWidth;
                    }
                    this.categorySwipe.translate(translateXValue, 0);
                }
                return true;
            }

            this.categorySwipe.translate(translateXValue, 0);

            return false;
        },

        /**
         * Change game portlet view mode
         * @param {Event} e
         */
        changeViewMode: function(e) {
            // add correct active class for navigation
            this.$('.fn-view-mode-item.active').removeClass('active');
            e.currentTarget.classList.add('active');

            if (this.options.viewMode !== e.currentTarget.dataset.viewMode) {
                this.options.viewMode = e.currentTarget.dataset.viewMode;
                this.renderGames();
                this.track(
                    'gamePortletView',
                    {
                        feedName: this.options.categoriesFeedName,
                        viewType: this.options.viewMode
                    }
                );
            }
        },

        /**
         * Change active category
         * @param  {Event} e
         */
        _onCategoryClick: function(e) {
            return this._changeCategory(e.currentTarget.dataset.category);
        },

        _changeCategory: function(newCategoryName, isNavigationOccurred) {
            var categories = this.model.categories;
            if (categories.getActiveName() === newCategoryName) {
                return;
            }

            this.track(
                'gamePortletCategoryChange',
                {
                    feedName: this.options.categoriesFeedName,
                    categoryName: newCategoryName
                }
            );

            this.model.set('sortField', 'default');

            categories.setActive(newCategoryName);
            if (!isNavigationOccurred) {
                this.model.setCategoryLocation(newCategoryName, this.options.filterChangePage);
            }
        },

        changeGameSorting: function(e) {
            this.track(
                'gamePortletSort',
                {
                    feedName: this.options.categoriesFeedName,
                    currentSortType: this.model.get('sortField'),
                    newSortType: e.target.value
                }
            );

            this.model.set('sortField', e.target.value);
        },

        _sortGamesOnJackpotsResponse: function() {
            if (this.model.get('sortField') === 'jackpotCode') {
                this.renderGames();
                return true;
            }
            return false;
        },

        /**
         * Handle game item tap
         * @param  {Event} e
         */
        handleGameItemTap: function(e) {
            var gameCode = e.currentTarget.dataset.gameCode;

            e.stopPropagation();

            this.showGameInfo(gameCode);
        },

        /**
         * Open game info popup
         * @param  {String} gameCode
         */
        showGameInfo: function(gameCode) {
            var cachedState = history.state;
            var friendlyGameName = config.get('gamesConfiguration.' + gameCode).seoFriendlyGameName;
            var href = '/igaming/games-info/' + encodeURIComponent(friendlyGameName || gameCode);
            var gameInfoRootPage = url.getCleanUrl(url.getPathname(url.href));

            core.trigger('game:launch:maybe', {
                gameCode: gameCode,
                feedName: this.options.categoriesFeedName,
                container: this.options.portletId,
                category: _.find(this.options.categories, function(cat) { return cat.active; })
            });

            storage.session.setItem('gamesPortletPreferences', this.model.toJSON());

            core.navigate(href, {
                state: {
                    gameInfoRootPage: gameInfoRootPage,
                    cachedState: cachedState
                }
            });
        },

        /**
         * Add/Remove favorite icon and refresh "Favorite" category
         * @private
         * @param  {Model} game
         */
        handleUpdateFavorites: function(game) {
            var gameCode = game.get('favoriteID');

            this.$('.fn-favorites[data-game-code="' + gameCode + '"]')
                .toggleClass('active', favoritesStore.hasItem(gameCode));
        },

        /**
         * Get active slide with games
         * @private
         * @return {Number}
         */
        _getActivePage: function() {
            var activePage = 0;

            if (this.$games && this.$games.isWidgetAttached('paging')) {
                activePage = this.$games.paging('option', 'activePage');
            }

            return activePage;
        },

        _onJackpotsChange: function() {
            if (!this._sortGamesOnJackpotsResponse()) {
                this._redrawGameJackpots();
            }
        },

        /**
         * Add jackpot tickers to games
         */
        _addGamesJackpots: function() {
            // For popup view this.el is not in DOM that why we defers function invoking
            _.defer(function() {
                // See ticker.widget.js for explanation
                if (!document.body.contains(this.el)) {
                    return;
                }

                var jackpots = jackpotModel.get('jackpots');

                if (jackpots && this.options.showJackpots) {
                    this.$('.fn-game-jackpot').forEach(function(jackpotNode) {
                        this._attachGameJackpotWidget(jackpots, $(jackpotNode));
                    }.bind(this));

                    this.$el.find('.fn-gamesinfo__detailed-container-with-jackpot')
                        .filter(function(jackpot) {
                            return $(jackpot).find('canvas').length;
                        })
                        .forEach(function(jackpot) {
                            jackpot.classList.add('shown-jackpot');
                        });

                    this.options.hasPaging && this._resumeVisibleGamesJackpots();
                }

                return this;
            }.bind(this));

        },

        /**
         * Remove jackpot tickers from games
         */
        _removeGamesJackpots: function() {
            this.$('.fn-game-jackpot').forEach(function(jackpotNode) {
                this.detachWidget(jackpotNode, 'ticker');
            }, this);
        },

        _redrawGameJackpots: function() {
            this._removeGamesJackpots();
            this._addGamesJackpots();
        },

        _attachGameJackpotWidget: function(jackpots, $jackpot) {
            var gameCode = $jackpot.data('gameCode');
            var jackpotCode = this.model.games.get(gameCode).get('jackpotCode');
            var jackpot = jackpots[jackpotCode] ? jackpots[jackpotCode][0] : null;

            if (jackpot) {
                jackpot.showGuaranteedHitTime = this.options.showGuaranteedHitTime;

                this.attachWidget($jackpot.ticker(jackpot));

                // Stop tickers if there are several pages in category for better performance
                this.options.hasPaging && $jackpot.ticker('pause');

                if (this.options.viewMode === 'ITEM') {
                    $jackpot.parent().removeClass('hidden');
                }
            }
        },

        /**
         * Get list of games with jackpots currently visible on page
         * @return Array of jBone
         */
        _getVisibleGamesJackpots: function() {
            return this.$('.fn-game-items')
                .eq(this._getActivePage())
                .find('.fn-game-jackpot')
                .filter(function(el) {
                    return $.isWidgetAttached(el, 'ticker');
                })
                .map($);
        },

        _stopGamesJackpots: function() {
            this.$('.fn-game-jackpot').map($).forEach(function($jackpotNode) {
                $jackpotNode.isWidgetAttached('ticker') && $jackpotNode.ticker('pause');
            });
        },

        /**
         * Resume jackpot tickers currently visible on page
         */
        _resumeVisibleGamesJackpots: function() {
            this._getVisibleGamesJackpots().forEach(function($jackpotNode) {
                $jackpotNode.ticker('resume');
            });
        },

        toggleGameFavorites: function(e) {
            e.stopImmediatePropagation();

            var $toggleFavoriteBtn = $(e.currentTarget);
            var gameCode = $toggleFavoriteBtn.data('gameCode');
            var favoriteId = this.model.games.get(gameCode).id;

            if ($toggleFavoriteBtn.hasClass('disabled')) {
                return;
            }

            this.track(
                'gameFavoritesChange',
                {
                    feedName: this.options.categoriesFeedName,
                    gameCode: gameCode,
                    method: !favoritesStore.hasItem(favoriteId) ? 'Add' : 'Remove'
                }
            );

            // invoked in favorites.mixin.js
            this.toggleFavorites({
                favoriteID: favoriteId,
                type: 'game'
            }, $toggleFavoriteBtn);
        },

        _updateLayoutMap: function() {
            var activeCategory = this.model.categories.getActive();
            this.layoutMap = layoutHelper.getMap(activeCategory.get('customLayout'), this.options);
            if (this.layoutMap) {
                _.extend(this.layoutMap, this._customLayoutCssValues);
            }
        },

        getIsCustomLayout: function() {
            return Boolean(this.layoutMap);
        },

        resizeCustomLayout: function() {
            if (!this.getIsCustomLayout() || this.options.viewMode !== 'ITEM') {
                return;
            }

            var $items = this.$('.fn-style-extended');
            if (!$items.length) {
                return;
            }

            // get 'em' size
            var itemWidthEm = this._customLayoutCssValues.cellWidth + this._customLayoutCssValues.cellMarginL
                + this._customLayoutCssValues.cellMarginR;
            var currentLayoutFirstItemWidthEm = this._customLayoutCssValues.cellWidth
                + (this.layoutMap.list[0].w - 1) * itemWidthEm;
            var currentLayoutFirstItemWidthPx = parseFloat(getComputedStyle($items[0]).width);
            var emSize = currentLayoutFirstItemWidthPx / currentLayoutFirstItemWidthEm;

            // get 'scaleFactor'
            var listWidthEm = this.layoutMap.gridWidth * itemWidthEm
                + this._customLayoutCssValues.containerPaddingL + this._customLayoutCssValues.containerPaddingR;
            var listWidthPx = listWidthEm * emSize;
            var $container = this.$('.fn-game-items');
            var containerWidthPx = parseFloat(getComputedStyle($container[0]).width);
            var scaleFactor = containerWidthPx / listWidthPx;

            // There is a bug in Chrome making content of a box to be blurred upon applying `box-shadow` to it with
            // `transform:scale`. If it is critical to get rid of this bug - you may uncomment line below and comment
            // `$resizeContainer::scale` a dozen lines below. Be aware that this will reintroduce another bug - with
            // incorrect container size for small number of columns (1 or 2 usually).
            // $container.css('transform', 'scale(' + scaleFactor + ')');

            var listBottomPx = $items.reduce(function(memo, tile) {
                var cs = getComputedStyle(tile);
                var bottom = parseFloat(cs.top) + parseFloat(cs.height);
                if (bottom > memo) {
                    memo = bottom;
                }

                return memo;
            }, 0);

            var marginBottomEm = this._customLayoutCssValues.cellMarginB;
            var calculatedHeightPx = listBottomPx * scaleFactor;
            var calculatedMarginEm = marginBottomEm * scaleFactor;
            $container.css('height', 'calc(' + calculatedHeightPx + 'px + ' + calculatedMarginEm + 'em)');

            var $resizeContainer = $container.find('.fn-resize-container');
            $resizeContainer.css('transform', 'scale(' + scaleFactor + ')');
            $resizeContainer.css('height', 'calc(' + listBottomPx + 'px + ' + marginBottomEm + 'em)');
            $resizeContainer.css('width', 'calc(100% / ' + scaleFactor + ')');
        },

        /**
         * Add widget for scaling game items to fit the screen
         */
        addScalingWidget: function() {
            var isAutoFill = this.options.responsiveGrid && this.options.autoFill;
            if (!isAutoFill || !this.portletHasMounted || this.getIsCustomLayout()) {
                return;
            }

            var $container = this.$('.fn-resize-container');
            var options = {};
            if (this.layoutMap) {
                options.itemsPerRow = this.layoutMap.gridWidth;
            }

            this.attachWidget($container.scale(options));
            this.fixPageHeight();
        },

        updateScalingWidget: function() {
            this.removeScalingWidget();
            this.addScalingWidget();
            this.setCategoryTabsWidth();
            this.handleGamesContainerResize();
            this.fixLastRowItemsDisplay();
        },

        removeScalingWidget: function() {
            this.$('.fn-resize-item').forEach(element => {
                element.style.width = "";
                element.style.height = "";
            });

            var $container = this.$('.fn-resize-container');
            this.detachWidget($container, 'scale');
        },

        /**
         * Handler for scaling widget
         */
        handleGamesContainerResize: function() {
            var $container = this.$('.fn-resize-container');

            $container.isWidgetAttached('scale') && $container.scale('scale');

            this.resizeCustomLayout();
            this.fixPageHeight();
        },

        close: function() {
            $(window).off('.gamesinfo');
            this.dynamicGameImages.disable();
            return PortletView.prototype.close.apply(this, arguments);
        }
    });
});
